import { Button } from "components/DesignSystem/Button/Button";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import Table from "components/DesignSystem/Table/V2/Table";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "react-table-8.10.7";
import {
  DocumentRequest,
  useUploadRequestedDocumentFilesMutation,
  useDeleteRequestedDocumentFileMutation,
  useGetDocumentRequestQuery,
} from "store/apis/requestDocument";
import {
  openRequestDocumentsModal,
  toggleDocumentRequestEdit,
} from "store/slices/requestDocumentsInChat";
import { RootState } from "store/store";
import { ArrayElement } from "types/utils/ArrayElement";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { Preview } from "components/PreviewModal";
import { actionItemsApi } from "store/apis/actionItems";
import { pluralize } from "utils/pluralize";
import { useWindowSize } from "hooks/useWindowSize";

type RequestedDocument = ArrayElement<DocumentRequest["requested_documents"]>;

const RequestedDocumentUpload = ({
  request,
}: {
  request: RequestedDocument;
}) => {
  const { successToast, alertToast } = useToast();
  const dispatch = useDispatch();

  const [previewFileUuid, setPreviewFileUuid] = useState<string | null>(null);
  const [deletingFileUuid, setDeletingFileUuid] = useState<string | null>(null);

  const groupId = useSelector(
    (state: RootState) => state.requestDocumentsInChat.groupId
  );

  const [uploadRequestedDocumentFiles, { isLoading: isUploading }] =
    useUploadRequestedDocumentFilesMutation();
  const [deleteRequestedDocumentFile, { isLoading: isDeleting }] =
    useDeleteRequestedDocumentFileMutation();

  const documents = request.documents;

  const refetchActionItems = () => {
    setTimeout(() => {
      dispatch(
        actionItemsApi.util.invalidateTags([{ type: "ALL_ACTION_ITEMS" }])
      );
    }, 1000);
  };

  const handleDrop = async (files: FileType[]) => {
    if (!groupId || files.length === 0) {
      return;
    }

    try {
      await uploadRequestedDocumentFiles({
        groupId,
        files: files as File[],
        requestedDocumentId: request.uuid,
      }).unwrap();
    } catch (error) {
      alertToast({ message: "Failed to upload, please try again!" });
      return;
    }

    successToast({
      message: `${pluralize(files.length, "File", "Files")} uploaded!`,
    });
    refetchActionItems();
  };

  const handleDelete = async (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number
  ) => {
    const file = documents?.[index];

    if (!groupId || !file) {
      return;
    }

    setDeletingFileUuid(file.uuid);

    try {
      await deleteRequestedDocumentFile({
        groupId,
        fileUuid: file.uuid,
      }).unwrap();
      successToast({ message: "File has been deleted!" });
      refetchActionItems();
    } catch (error) {
      alertToast({ message: "Failed to delete, please try again!" });
    }

    setDeletingFileUuid(null);
  };

  const handleFileClick = ({ uuid }: { uuid: string }) =>
    setPreviewFileUuid(uuid);

  const closePreview = () => setPreviewFileUuid(null);

  return (
    <>
      <FileInput
        multiple
        files={documents}
        onFileClick={handleFileClick}
        onDelete={handleDelete}
        onDrop={handleDrop}
        isUploading={isUploading}
        isDeleting={isDeleting}
        fileDeletingUuid={deletingFileUuid ?? undefined}
      />
      <Preview
        showModal={Boolean(previewFileUuid)}
        closeModal={closePreview}
        previewId={previewFileUuid}
      />
    </>
  );
};

const MobileRequestedDocumentCard = ({
  request,
}: {
  request: RequestedDocument;
}) => (
  <div className="t-flex t-flex-col t-gap-2 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-3">
    <div className="t-text-subtext-sm t-text-text-60">
      {request.other_name || request.name}
    </div>
    {request.description && (
      <div className="t-break-words t-text-body-sm t-text-text-30">
        {request.description}
      </div>
    )}
    <RequestedDocumentUpload request={request} />
  </div>
);

const UploadRequestedDocuments = () => {
  const { isMobile } = useWindowSize();
  const groupId = useSelector(
    (state: RootState) => state.requestDocumentsInChat.groupId
  );
  const requestId = useSelector(
    (state: RootState) => state.requestDocumentsInChat.requestId
  );

  const { data: documentsRequest, isLoading } = useGetDocumentRequestQuery(
    {
      groupId: groupId!,
      requestId: requestId!,
    },
    { skip: !groupId || !requestId, refetchOnMountOrArgChange: true }
  );

  const columnHelper = createColumnHelper<RequestedDocument>();

  const columns = [
    columnHelper.display({
      id: "name",
      header: "Document Name",
      size: 30,
      cell: (info) => (
        <div className="t-pt-3">
          {info.row.original.other_name || info.row.original.name}
        </div>
      ),
    }),
    columnHelper.accessor("description", {
      header: "Description",
      size: 50,
      cell: (info) => (
        <div className="t-break-all t-pt-3">{info.getValue()}</div>
      ),
    }),
    columnHelper.display({
      id: "upload",
      header: "Upload document",
      size: 50,
      cell: (info) => <RequestedDocumentUpload request={info.row.original} />,
    }),
  ];

  const reversedDocuments = useMemo(
    () => [...(documentsRequest?.requested_documents || [])].reverse(),
    [documentsRequest?.requested_documents]
  );

  const table = useReactTable({
    data: reversedDocuments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 5,
      maxSize: 100,
    },
  });

  if (isLoading) {
    return (
      <div className="t-flex t-justify-center t-items-center t-min-h-56">
        <Loader size="small" />
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="t-flex t-flex-col t-gap-3">
        {reversedDocuments.map((request) => (
          <MobileRequestedDocumentCard key={request.uuid} request={request} />
        ))}
      </div>
    );
  }

  return (
    <Table.Container>
      <Table.Content>
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.HeadCell
                  key={header.id}
                  style={{
                    width: `${header.getSize()}%`,
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </Table.HeadCell>
              ))}
            </Table.Row>
          ))}
        </Table.Head>
        <Table.Body>
          {table.getRowModel().rows.map((row) => (
            <Table.Row key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Table.Cell
                  key={cell.id}
                  style={{
                    width: `${cell.column.getSize()}%`,
                    verticalAlign: "top",
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Content>
    </Table.Container>
  );
};

export const UploadMode = () => {
  const isOpen = useSelector(
    (state: RootState) => state.requestDocumentsInChat.isModalOpen
  );
  const documentsRequest = useSelector(
    (state: RootState) => state.requestDocumentsInChat.documentsRequest
  );
  const { isCpa } = useRoleBasedView();

  const dispatch = useDispatch();

  const closeModal = () =>
    dispatch(
      openRequestDocumentsModal({ isModalOpen: false, channelId: null })
    );

  const onEditClick = () => dispatch(toggleDocumentRequestEdit(true));

  const modalTitle = documentsRequest?.every((d) => d.is_uploaded)
    ? "Uploaded Documents"
    : "Upload Requested Documents";

  return (
    <Modal.Root open={isOpen} onOpenChange={closeModal}>
      <Modal.Content size="xxl">
        <Modal.Header>
          <Modal.Title>{modalTitle}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="sm:t-px-4">
          <UploadRequestedDocuments />
        </Modal.Body>
        {isCpa ? (
          <Modal.Footer>
            <div className="t-flex t-justify-between t-items-center t-w-full">
              <Button onClick={onEditClick}>Edit</Button>
            </div>
          </Modal.Footer>
        ) : (
          <Modal.FooterButtonGroup>
            <Button onClick={closeModal}>Close</Button>
          </Modal.FooterButtonGroup>
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
